import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { getSupabase } from "../../utils";
import { reconcileManifestWithText } from "@/lib/utils/crm/template-variables";
import {
  EmailTemplate,
  EmailTemplateSearchParams
} from "./types";

const emailTemplatesQueryKey = "email-templates";

// Fetch all email templates through server-side RPC filtering
async function fetchEmailTemplates(
  folderId?: string,
  search?: EmailTemplateSearchParams
): Promise<EmailTemplate[]> {
  const supabase = getSupabase();
  const effectiveFolderId = search?.folderId ?? folderId;
  const { data, error } = await supabase.rpc("search_email_templates", {
    p_query: search?.q?.trim() || null,
    p_folder_id: effectiveFolderId ?? null,
    p_is_shared: search?.isShared ?? null,
    p_name: search?.name?.trim() || null,
    p_subject: search?.subject?.trim() || null,
    p_tags: search?.tags && search.tags.length > 0 ? search.tags : null,
    p_limit: 1000,
    p_offset: 0,
  });

  if (error) {
    throw new Error(error.message);
  }

  return ((data || []) as Array<
    EmailTemplate & { folder_name?: string | null; folder_parent_id?: string | null }
  >).map((row) => ({
    ...row,
    folder: row.folder_id
      ? {
          id: row.folder_id,
          name: row.folder_name ?? "Unknown Folder",
          parent_id: row.folder_parent_id ?? null,
        }
      : null,
  }));
}

export function useEmailTemplates(folderId?: string, search?: EmailTemplateSearchParams) {
  return useQuery<EmailTemplate[]>({
    queryKey: [emailTemplatesQueryKey, { folderId, search }],
    queryFn: () => fetchEmailTemplates(folderId, search),
  });
}

// Fetch a single email template
async function fetchEmailTemplate(id: string): Promise<EmailTemplate> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("email_templates")
    .select("*, folder:folder_id(id,name,parent_id)")
    .eq("id", id)
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export function useEmailTemplate(id: string, enabled: boolean = true) {
  return useQuery<EmailTemplate>({
    queryKey: [emailTemplatesQueryKey, id],
    queryFn: () => fetchEmailTemplate(id),
    enabled: enabled && !!id,
  });
}

// Create a new email template
type CreateEmailTemplatePayload = Omit<EmailTemplate, "id" | "created_at" | "user_id" | "folder">;

async function createEmailTemplate(payload: CreateEmailTemplatePayload): Promise<EmailTemplate> {
  const supabase = getSupabase();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  // Always store a complete, non-stale variable manifest. `payload.variables` is
  // the author's declared manifest (or undefined on a plain create); reconcile it
  // against the tokens actually present in the subject/body so the template can
  // never send with an unresolved {{variable}} (EMAIL_TEMPLATES_CANONICAL_SYSTEM).
  const { variables: authoredManifest, ...rest } = payload;
  const variables = reconcileManifestWithText(
    rest.subject,
    rest.body,
    authoredManifest ?? null
  );

  const { data, error } = await supabase
    .from("email_templates")
    .insert([{ ...rest, variables, user_id: user.id }])
    .select("*, folder:folder_id(id,name,parent_id)")
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export function useCreateEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEmailTemplate,
    onSuccess: () => {
      toast.success("Email template created successfully");
      void queryClient.invalidateQueries({ queryKey: [emailTemplatesQueryKey] });
    },
    onError: (error) => {
      toast.error(`Failed to create template: ${error.message}`);
    },
  });
}

// Update an email template
type UpdateEmailTemplatePayload = Partial<CreateEmailTemplatePayload> & { id: string };

async function updateEmailTemplate(payload: UpdateEmailTemplatePayload): Promise<EmailTemplate> {
  const { id, variables: authoredManifest, ...updateData } = payload;
  logger.info("Updating email template", { id, updateData });

  const supabase = getSupabase();

  // Check current user
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) {
    logger.error("User auth error", userError);
    throw new Error("Authentication error");
  }

  if (!user) {
    logger.error("No authenticated user");
    throw new Error("User not authenticated");
  }

  logger.info("Current user", { id: user.id, email: user.email });

  // First, check if the template exists and belongs to the current user
  const { data: existingTemplate, error: fetchError } = await supabase
    .from("email_templates")
    .select("user_id")
    .eq("id", id)
    .single();

  if (fetchError) {
    logger.error("Error fetching template", fetchError);
    throw new Error("Template not found");
  }

  if (existingTemplate.user_id !== user.id) {
    logger.error("Template ownership mismatch", {
      templateUserId: existingTemplate.user_id,
      currentUserId: user.id,
    });
    throw new Error("You do not have permission to update this template");
  }

  // Recompute the stored manifest from the latest subject/body so author edits to
  // per-variable settings persist AND the manifest never drifts from the text.
  // Only touch `variables` when this update actually carries the text (partial
  // updates that omit subject/body must not wipe the tokens).
  const finalUpdate: Record<string, unknown> = { ...updateData };
  if (typeof updateData.subject === "string" && typeof updateData.body === "string") {
    finalUpdate.variables = reconcileManifestWithText(
      updateData.subject,
      updateData.body,
      authoredManifest ?? null
    );
  }

  const { data, error } = await supabase
    .from("email_templates")
    .update(finalUpdate)
    .eq("id", id)
    .select("*, folder:folder_id(id,name,parent_id)")
    .single();

  if (error) {
    logger.error("Supabase update error", error);
    throw new Error(error.message);
  }

  logger.info("Update successful", { data });
  return data;
}

export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateEmailTemplate,
    onSuccess: (data) => {
      toast.success("Email template updated successfully");
      void queryClient.invalidateQueries({ queryKey: [emailTemplatesQueryKey] });
      queryClient.setQueryData([emailTemplatesQueryKey, data.id], data);
    },
    onError: (error) => {
      toast.error(`Failed to update template: ${error.message}`);
    },
  });
}
